import type { UseQueryResult } from '@tanstack/react-query'
import { queryOptions, useQuery } from '@tanstack/react-query'
import { COMMUNITY_OPEN_GLOBAL_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { UseQueryClearOptions } from 'shared/types/tanstack-utility-types'
import { addQueryParam } from 'shared/utils/add-query-param'
import type { InvitationValidityInterface } from 'modules/community/types/invitation-interface'
import { communityKeys } from './key-factory'

interface UseInvitationValidityOptions extends Omit<
  UseQueryClearOptions<InvitationValidityInterface, Error, InvitationValidityInterface>,
  'enabled' | 'retry'
> {
  communityPath: string
  invitationToken: string
  confirmationToken?: string
}

export const useInvitationValidity = ({
  communityPath,
  invitationToken,
  confirmationToken,
  ...options
}: UseInvitationValidityOptions): UseQueryResult<InvitationValidityInterface, Error> => {
  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.get,
    InvitationValidityInterface
  >({
    method: RequestMethodsEnum.get,
    notFoundResponseHandler: () => {},
    accessDeniedResponseHandler: error => {
      throw error
    },
    unauthorizedResponseHandler: () => {},
  })

  return useQuery(
    queryOptions({
      queryKey: communityKeys.invitations.validity(
        communityPath,
        invitationToken,
        confirmationToken ?? '',
      ),
      queryFn: () => {
        const search = new URLSearchParams()
        addQueryParam(search, 'invitationToken', invitationToken)
        addQueryParam(search, 'confirmationToken', confirmationToken)
        return fetcher(
          `${COMMUNITY_OPEN_GLOBAL_PREFIX}/${communityPath}/invitation-validity?${search}`,
        )
      },
      enabled: Boolean(communityPath && invitationToken),
      retry: false,
      refetchOnWindowFocus: false,
      ...options,
    }),
  )
}
